import React, { useState, useEffect, useCallback, useRef } from 'react';
import { customerService } from '../services/customerService';
import CustomerHistoryModal from '../components/CustomerHistoryModal';
import CustomerModal from '../components/CustomerModal';
import { useLocation } from '../contexts/LocationContext';
import { usePermissions } from '../contexts/PermissionsContext';
import ActionMenu from '../components/common/ActionMenu';

// Wspolne style zgodne ze standardem UI projektu
const btnStyle = {
  padding: '4px 10px',
  fontSize: '11px',
  border: '1px solid var(--border)',
  borderRadius: '4px',
  cursor: 'pointer',
  backgroundColor: 'var(--card-bg)',
  color: 'var(--text-secondary)'
};

const thStyle = {
  padding: '0.4rem 0.6rem',
  textAlign: 'left',
  borderBottom: '2px solid var(--border)',
  fontWeight: '600',
  color: 'var(--text-secondary)'
};

const tdStyle = { padding: '0.4rem 0.6rem', borderBottom: '1px solid var(--border)' };

const ROLE_FILTERS = [
  { key: 'odbiorca', label: 'Odbiorcy' },
  { key: 'dostawca', label: 'Dostawcy' },
  { key: '', label: 'Wszyscy' }
];

// Nazwa wyswietlana kontrahenta - firma ma pierwszenstwo
const displayName = (c) => {
  if (!c) return '';
  return c.nazwa_firmy || `${c.imie || ''} ${c.nazwisko || ''}`.trim() || `#${c.id}`;
};

// Znacznik roli - odbiorca / dostawca (moga byc obie naraz)
const RoleBadges = ({ customer }) => {
  const odbiorca = customer.jest_odbiorca === undefined ? true : !!Number(customer.jest_odbiorca);
  const dostawca = !!Number(customer.jest_dostawca);
  const badge = (text, bg, color) => (
    <span key={text} style={{
      display: 'inline-block', fontSize: '10px', padding: '2px 6px',
      borderRadius: '4px', backgroundColor: bg, color: color, marginRight: '4px'
    }}>{text}</span>
  );
  if (!odbiorca && !dostawca) {
    return <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>—</span>;
  }
  return (
    <>
      {odbiorca && badge('Odbiorca', 'var(--info-light)', 'var(--info)')}
      {dostawca && badge('Dostawca', 'var(--purple-light)', 'var(--purple)')}
    </>
  );
};

// Zgody marketingowe - SMS i e-mail rozdzielone
const ConsentBadges = ({ customer }) => {
  const sms = !!Number(customer.zgoda_sms);
  const mail = !!Number(customer.zgoda_email);
  const item = (text, on, title) => (
    <span title={title} style={{
      display: 'inline-block', fontSize: '10px', padding: '2px 6px', borderRadius: '4px',
      border: `1px solid ${on ? 'var(--success)' : 'var(--border)'}`,
      color: on ? 'var(--success)' : 'var(--text-muted)',
      marginRight: '4px'
    }}>{text}</span>
  );
  return (
    <>
      {item('SMS', sms, sms ? 'Zgoda na SMS' : 'Brak zgody na SMS')}
      {item('@', mail, mail ? 'Zgoda na e-mail' : 'Brak zgody na e-mail')}
    </>
  );
};

const CustomersPage = () => {
  const { locationId } = useLocation();
  const { hasPermission } = usePermissions();

  const canEditCustomers = hasPermission('klienci', 'edycja', locationId);
  const canDeleteCustomers = hasPermission('klienci', 'usuwanie', locationId);
  const canAddCustomers = hasPermission('klienci', 'dodawanie', locationId);
  const canMergeCustomers = hasPermission('klienci', 'scalanie', locationId);

  const [customers, setCustomers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [rolaFilter, setRolaFilter] = useState('odbiorca');
  const [pokazNieaktywne, setPokazNieaktywne] = useState(false);

  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [stats, setStats] = useState({ total: 0, with_email: 0, with_phone: 0, suppliers: 0 });

  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedCustomerForHistory, setSelectedCustomerForHistory] = useState(null);

  const [sortColumn, setSortColumn] = useState('nazwa');
  const [sortDirection, setSortDirection] = useState('asc');

  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(20);

  // Scalanie kart
  const [mergeSource, setMergeSource] = useState(null);
  const [mergeQuery, setMergeQuery] = useState('');
  const [mergeResults, setMergeResults] = useState([]);
  const [mergeTarget, setMergeTarget] = useState(null);
  const [mergePowod, setMergePowod] = useState('');
  const [mergeBusy, setMergeBusy] = useState(false);
  const [mergeError, setMergeError] = useState(null);

  const [newCustomer, setNewCustomer] = useState(() => emptyCustomer());

  const firstLoad = useRef(true);

  function emptyCustomer() {
    return {
      typ_klienta: 'osoba_fizyczna',
      imie: '', nazwisko: '', email: '',
      telefon_kod: '+48', telefon: '',
      adres: '', miasto: '', kod_pocztowy: '', kraj: 'Polska', kraj_kod: 'PL',
      nip: '', regon: '', nazwa_firmy: '', uwagi: '',
      jest_odbiorca: 1, jest_dostawca: 0,
      zgoda_sms: 0, zgoda_email: 0,
      aktywny: 1,
      wysylka_inna: false, wysylka_imie: '', wysylka_nazwisko: '', wysylka_firma: '',
      wysylka_ulica: '', wysylka_kod_pocztowy: '', wysylka_miasto: '',
      wysylka_kraj: 'Polska', wysylka_kraj_kod: 'PL',
      wysylka_telefon_kod: '+48', wysylka_telefon: '',
      karta_lojalnosciowa_1: '', karta_lojalnosciowa_2: ''
    };
  }

  // Debounce pola szukania (300 ms) - wyszukiwanie idzie na serwer
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reset strony przy zmianie kryteriow
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, rolaFilter, pokazNieaktywne, perPage, sortColumn, sortDirection]);

  const fetchCustomers = useCallback(async () => {
    try {
      setLoading(true);
      const res = await customerService.getCustomers({
        search: debouncedSearch,
        rola: rolaFilter,
        aktywny: pokazNieaktywne ? 'all' : 1,
        page: currentPage,
        per_page: perPage,
        sort: sortColumn,
        sort_dir: sortDirection
      });
      setCustomers(res.items);
      setTotal(res.total);
      setError(null);
    } catch (err) {
      setError('Błąd: ' + err.message);
      setCustomers([]);
      setTotal(0);
    } finally {
      setLoading(false);
      firstLoad.current = false;
    }
  }, [debouncedSearch, rolaFilter, pokazNieaktywne, currentPage, perPage, sortColumn, sortDirection]);

  useEffect(() => { fetchCustomers(); }, [fetchCustomers]);

  const fetchStats = useCallback(async () => {
    try {
      const data = await customerService.getStats();
      setStats({
        total: data.total_customers ?? data.total ?? 0,
        with_email: data.customers_with_email ?? data.with_email ?? 0,
        with_phone: data.customers_with_phone ?? data.with_phone ?? 0,
        suppliers: data.total_suppliers ?? data.suppliers ?? 0
      });
    } catch (err) {
      // Statystyki sa dodatkiem - brak statystyk nie moze blokowac listy
      console.warn('Nie udało się pobrać statystyk kartoteki:', err.message);
    }
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const refreshAll = () => { fetchCustomers(); fetchStats(); };

  // Zapis nowej karty. Blad przekazujemy dalej - modal obsluguje konflikt NIP (409).
  const handleAddCustomer = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const customerData = {
      ...newCustomer,
      telefon: newCustomer.telefon ? `${newCustomer.telefon_kod || '+48'}${String(newCustomer.telefon).replace(/^\+\d+/, '')}` : '',
      wysylka_telefon: newCustomer.wysylka_telefon ? `${newCustomer.wysylka_telefon_kod || '+48'}${String(newCustomer.wysylka_telefon).replace(/^\+\d+/, '')}` : '',
      karta_lojalnosciowa_1: newCustomer.karta_lojalnosciowa_1 || '',
      karta_lojalnosciowa_2: newCustomer.karta_lojalnosciowa_2 || ''
    };
    // Kartoteka jest wspolna dla sieci - NIE wysylamy location_id
    delete customerData.location_id;
    await customerService.addCustomer(customerData);
    setNewCustomer(emptyCustomer());
    setShowAddModal(false);
    refreshAll();
  };

  const handleUpdateCustomer = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const customerData = {
      ...editingCustomer,
      telefon: editingCustomer.telefon ? `${editingCustomer.telefon_kod || '+48'}${String(editingCustomer.telefon).replace(/^\+\d+/, '')}` : '',
      wysylka_telefon: editingCustomer.wysylka_telefon ? `${editingCustomer.wysylka_telefon_kod || '+48'}${String(editingCustomer.wysylka_telefon).replace(/^\+\d+/, '')}` : '',
      karta_lojalnosciowa_1: editingCustomer.karta_lojalnosciowa_1 || '',
      karta_lojalnosciowa_2: editingCustomer.karta_lojalnosciowa_2 || ''
    };
    // Kartoteka jest wspolna dla sieci - NIE wysylamy location_id
    delete customerData.location_id;
    await customerService.updateCustomer(editingCustomer.id, customerData);
    setShowEditModal(false);
    setEditingCustomer(null);
    refreshAll();
  };

  // Otworz istniejaca karte wskazana przez backend przy konflikcie NIP
  const handleOpenExisting = async (existing) => {
    try {
      const full = await customerService.getCustomer(existing.id);
      setShowAddModal(false);
      setEditingCustomer(toEditModel(full || existing));
      setShowEditModal(true);
    } catch (err) {
      setError('Błąd: ' + err.message);
    }
  };

  // Backend zwraca adres pod DWIEMA nazwami ('ulica' i alias 'ulica AS adres'),
  // a formularz edytuje wylacznie 'adres'. Zostawienie obu kluczy w payloadzie
  // powodowalo, ze zapis szedl ze stara wartoscia 'ulica'. Wysylamy tylko 'adres'.
  const toEditModel = (customer) => {
    const model = {
      ...customer,
      typ_klienta: customer.typ_klienta || (customer.nazwa_firmy ? 'firma' : 'osoba_fizyczna'),
      adres: customer.ulica || customer.adres || ''
    };
    delete model.ulica;
    return model;
  };

  const handleDeactivateCustomer = async (customer) => {
    const nazwa = displayName(customer);
    if (!window.confirm(`Dezaktywować kartę "${nazwa}"?\n\nKarta zniknie z list wyboru, ale dokumenty historyczne zostaną nietknięte.`)) {
      return;
    }
    try {
      const res = await customerService.deleteCustomer(customer.id);
      const d = res?.data || res || {};
      const parts = [];
      if (d.transakcje !== undefined) parts.push(`paragony: ${d.transakcje}`);
      if (d.zamowienia !== undefined) parts.push(`zamówienia: ${d.zamowienia}`);
      if (d.faktury_sprzedazy !== undefined) parts.push(`faktury sprzedaży: ${d.faktury_sprzedazy}`);
      if (d.faktury_zakupu !== undefined) parts.push(`faktury zakupu: ${d.faktury_zakupu}`);
      if (d.reklamacje !== undefined) parts.push(`reklamacje: ${d.reklamacje}`);
      const podsumowanie = parts.length
        ? ` Powiązane dokumenty pozostają nienaruszone (${parts.join(', ')}).`
        : '';
      setNotice(`Karta "${nazwa}" została dezaktywowana.${podsumowanie}`);
      refreshAll();
    } catch (err) {
      setError('Błąd: ' + err.message);
    }
  };

  // --- Scalanie kart ---
  const openMerge = (customer) => {
    setMergeSource(customer);
    setMergeTarget(null);
    setMergeQuery('');
    setMergeResults([]);
    setMergePowod('');
    setMergeError(null);
  };

  const closeMerge = () => {
    setMergeSource(null);
    setMergeTarget(null);
    setMergeResults([]);
    setMergeError(null);
  };

  useEffect(() => {
    if (!mergeSource) return undefined;
    const term = mergeQuery.trim();
    if (term.length < 2) { setMergeResults([]); return undefined; }
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const res = await customerService.searchCustomers(term, 15, '');
        if (cancelled) return;
        const list = res?.data?.customers || res?.customers || [];
        setMergeResults((Array.isArray(list) ? list : []).filter(c => c.id !== mergeSource.id));
      } catch (err) {
        if (!cancelled) setMergeError(err.message);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [mergeQuery, mergeSource]);

  const handleMergeConfirm = async () => {
    if (!mergeSource || !mergeTarget) return;
    const ostrzezenie = 'Operacja przepina dokumenty; nazwa klienta na starych paragonach zmieni się.\n\n'
      + `Karta źródłowa: ${displayName(mergeSource)} (#${mergeSource.id}) zostanie oznaczona jako scalona i dezaktywowana.\n`
      + `Karta docelowa: ${displayName(mergeTarget)} (#${mergeTarget.id}) przejmie wszystkie dokumenty.\n\nKontynuować?`;
    if (!window.confirm(ostrzezenie)) return;
    try {
      setMergeBusy(true);
      setMergeError(null);
      await customerService.scalCustomers(mergeTarget.id, mergeSource.id, mergePowod);
      setNotice(`Scalono kartę #${mergeSource.id} z kartą #${mergeTarget.id} (${displayName(mergeTarget)}).`);
      closeMerge();
      refreshAll();
    } catch (err) {
      setMergeError(err.message);
    } finally {
      setMergeBusy(false);
    }
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const SortIcon = ({ column }) => {
    if (sortColumn !== column) {
      return <span style={{ opacity: 0.3, marginLeft: '4px' }}>↕</span>;
    }
    return <span style={{ marginLeft: '4px' }}>{sortDirection === 'asc' ? '↑' : '↓'}</span>;
  };

  const totalPages = Math.max(1, Math.ceil(total / perPage));
  const startIndex = (currentPage - 1) * perPage;

  const handlePageChange = (page) => {
    const target = Math.min(Math.max(1, page), totalPages);
    setCurrentPage(target);
  };

  if (loading && firstLoad.current) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>⏳</div>
        <div style={{ color: 'var(--text-muted)', fontSize: '12px' }}>Ładowanie kartoteki...</div>
      </div>
    );
  }

  return (
    <div style={{ fontSize: '12px' }}>
      {/* Naglowek */}
      <div className="card" style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '4px' }}>Kartoteka kontrahentów</h2>
            <p style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
              Odbiorcy i dostawcy w jednej kartotece | {new Date().toLocaleDateString()}
            </p>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            {canAddCustomers && (
              <button className="btn btn-primary" onClick={() => { setNewCustomer(emptyCustomer()); setShowAddModal(true); }}>
                Dodaj kontrahenta
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Blad */}
      {error && (
        <div style={{
          padding: '0.5rem 0.75rem', backgroundColor: 'var(--danger-light)', color: 'var(--danger)',
          borderRadius: '4px', marginBottom: '0.5rem', fontSize: '11px'
        }}>❌ {error} <button onClick={() => setError(null)} style={{ float: 'right', background: 'none', border: 'none', color: 'var(--danger)', cursor: 'pointer' }}>×</button></div>
      )}

      {/* Komunikat informacyjny */}
      {notice && (
        <div style={{
          padding: '0.5rem 0.75rem', backgroundColor: 'var(--info-light)', color: 'var(--info)',
          borderRadius: '4px', marginBottom: '0.5rem', fontSize: '11px'
        }}>ℹ️ {notice} <button onClick={() => setNotice(null)} style={{ float: 'right', background: 'none', border: 'none', color: 'var(--info)', cursor: 'pointer' }}>×</button></div>
      )}

      {/* Statystyki (liczone na serwerze) */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
        <div style={{
          backgroundColor: 'var(--card-bg)', padding: '0.4rem 0.6rem', borderRadius: '4px',
          boxShadow: '0 1px 2px rgba(0,0,0,0.08)', borderLeft: '3px solid var(--info)',
          display: 'flex', alignItems: 'center', gap: '0.5rem'
        }}>
          <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>Wszystkich:</span>
          <span style={{ fontSize: '14px', fontWeight: '700', color: 'var(--primary)' }}>{stats.total}</span>
        </div>
        <div style={{
          backgroundColor: 'var(--card-bg)', padding: '0.4rem 0.6rem', borderRadius: '4px',
          boxShadow: '0 1px 2px rgba(0,0,0,0.08)', borderLeft: '3px solid var(--purple)',
          display: 'flex', alignItems: 'center', gap: '0.5rem'
        }}>
          <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>Dostawców:</span>
          <span style={{ fontSize: '14px', fontWeight: '700', color: 'var(--purple)' }}>{stats.suppliers}</span>
        </div>
        <div style={{
          backgroundColor: 'var(--card-bg)', padding: '0.4rem 0.6rem', borderRadius: '4px',
          boxShadow: '0 1px 2px rgba(0,0,0,0.08)', borderLeft: '3px solid var(--success)',
          display: 'flex', alignItems: 'center', gap: '0.5rem'
        }}>
          <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>Z e-mailem:</span>
          <span style={{ fontSize: '14px', fontWeight: '700', color: 'var(--success)' }}>{stats.with_email}</span>
        </div>
        <div style={{
          backgroundColor: 'var(--card-bg)', padding: '0.4rem 0.6rem', borderRadius: '4px',
          boxShadow: '0 1px 2px rgba(0,0,0,0.08)', borderLeft: '3px solid var(--info)',
          display: 'flex', alignItems: 'center', gap: '0.5rem'
        }}>
          <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>Z telefonem:</span>
          <span style={{ fontSize: '14px', fontWeight: '700', color: 'var(--info)' }}>{stats.with_phone}</span>
        </div>
      </div>

      {/* Filtry: rola + szukanie + nieaktywne */}
      <div style={{
        backgroundColor: 'var(--card-bg)', padding: '0.5rem 0.75rem', borderRadius: '4px',
        marginBottom: '0.5rem', boxShadow: '0 1px 2px rgba(0,0,0,0.08)'
      }}>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Filtr roli - toggle z pelnym tlem dla aktywnego */}
          <div style={{ display: 'flex', gap: '4px' }}>
            {ROLE_FILTERS.map(f => {
              const active = rolaFilter === f.key;
              return (
                <button
                  key={f.key || 'wszyscy'}
                  onClick={() => setRolaFilter(f.key)}
                  style={{
                    padding: '4px 10px',
                    fontSize: '11px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontWeight: active ? '600' : '400',
                    border: `1px solid ${active ? 'var(--primary)' : 'var(--border)'}`,
                    backgroundColor: active ? 'var(--primary)' : 'var(--card-bg)',
                    color: active ? '#fff' : 'var(--text-secondary)'
                  }}
                >
                  {f.label}
                </button>
              );
            })}
          </div>

          <span style={{ fontSize: '12px' }}>🔍</span>
          <input
            type="text"
            placeholder="Szukaj po nazwie, NIP, e-mailu, telefonie, numerze karty..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            style={{
              flex: 1, minWidth: '220px', padding: '6px 12px', fontSize: '12px',
              border: '1px solid var(--border)', borderRadius: '6px',
              backgroundColor: 'var(--card-bg)', color: 'var(--text)', outline: 'none'
            }}
          />
          {searchTerm && (
            <button onClick={() => setSearchTerm('')} style={{
              ...btnStyle, backgroundColor: 'var(--text-muted)', color: '#fff', border: 'none'
            }}>✕</button>
          )}

          <label style={{
            display: 'flex', alignItems: 'center', gap: '4px',
            fontSize: '11px', color: 'var(--text-secondary)', cursor: 'pointer'
          }}>
            <input
              type="checkbox"
              checked={pokazNieaktywne}
              onChange={e => setPokazNieaktywne(e.target.checked)}
              style={{ width: '14px', height: '14px', cursor: 'pointer' }}
            />
            Pokaż nieaktywne / scalone
          </label>
        </div>
      </div>

      {/* Tabela */}
      <div style={{ backgroundColor: 'var(--card-bg)', borderRadius: '4px', boxShadow: '0 1px 2px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
        <div style={{
          padding: '0.5rem 0.75rem', borderBottom: '1px solid var(--border)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center'
        }}>
          <div style={{ fontWeight: '600', fontSize: '12px' }}>
            📋 Lista kontrahentów
            <span style={{ marginLeft: '8px', padding: '2px 8px', border: '1px solid var(--primary)', color: 'var(--primary)', borderRadius: '10px', fontSize: '10px' }}>
              {total}
            </span>
            {loading && <span style={{ marginLeft: '8px', fontSize: '10px', color: 'var(--text-muted)' }}>⏳ ładowanie...</span>}
          </div>
          <button onClick={refreshAll} style={btnStyle}>🔄 Odśwież</button>
        </div>

        {customers.length === 0 ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '12px' }}>
            <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>👥</div>
            <div>Brak kontrahentów spełniających kryteria</div>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
              <thead>
                <tr style={{ backgroundColor: 'var(--bg)' }}>
                  <th style={{ ...thStyle, textAlign: 'center', width: '40px' }}>LP</th>
                  <th style={{ ...thStyle, cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('nazwa')}>Kontrahent<SortIcon column="nazwa" /></th>
                  <th style={{ ...thStyle, width: '150px' }}>Rola</th>
                  <th style={{ ...thStyle, cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('email')}>Kontakt<SortIcon column="email" /></th>
                  <th style={{ ...thStyle, cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('miasto')}>Adres<SortIcon column="miasto" /></th>
                  <th style={{ ...thStyle, width: '100px' }}>Zgody</th>
                  <th style={{ ...thStyle, textAlign: 'center', width: '80px' }}>Akcje</th>
                </tr>
              </thead>
              <tbody>
                {customers.map((customer, idx) => {
                  const scalona = !!customer.scalony_z_id;
                  const nieaktywna = customer.aktywny !== undefined && Number(customer.aktywny) === 0;
                  return (
                    <tr key={customer.id} style={{
                      backgroundColor: idx % 2 === 0 ? 'var(--card-bg)' : 'var(--bg)',
                      opacity: (scalona || nieaktywna) ? 0.6 : 1
                    }}>
                      <td style={{ ...tdStyle, textAlign: 'center', color: 'var(--text-muted)', fontSize: '10px' }}>
                        {startIndex + idx + 1}
                      </td>
                      <td style={tdStyle}>
                        <div style={{ fontWeight: '600' }}>
                          {customer.nazwa_firmy ? (
                            <><span style={{ color: 'var(--text-muted)' }}>🏢</span> {customer.nazwa_firmy}</>
                          ) : (
                            <><span style={{ color: 'var(--text-muted)' }}>👤</span> {customer.imie} {customer.nazwisko}</>
                          )}
                        </div>
                        {customer.nazwa_firmy && customer.imie && (
                          <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>{customer.imie} {customer.nazwisko}</div>
                        )}
                        {customer.nip && <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>NIP: {customer.nip}</div>}
                        {customer.numer_klienta && <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>Nr: {customer.numer_klienta}</div>}
                        {scalona && (
                          <div style={{
                            marginTop: '2px', display: 'inline-block', fontSize: '10px', padding: '2px 6px',
                            borderRadius: '4px', backgroundColor: 'var(--warning-light)', color: 'var(--warning)'
                          }}>
                            scalona z #{customer.scalony_z_id}
                          </div>
                        )}
                        {!scalona && nieaktywna && (
                          <div style={{
                            marginTop: '2px', display: 'inline-block', fontSize: '10px', padding: '2px 6px',
                            borderRadius: '4px', backgroundColor: 'var(--danger-light)', color: 'var(--danger)'
                          }}>
                            nieaktywna
                          </div>
                        )}
                      </td>
                      <td style={tdStyle}><RoleBadges customer={customer} /></td>
                      <td style={{ ...tdStyle, fontSize: '10px' }}>
                        {customer.email && <div>📧 {customer.email}</div>}
                        {customer.telefon && <div>📱 {customer.telefon}</div>}
                        {!customer.email && !customer.telefon && <span style={{ color: 'var(--text-muted)' }}>—</span>}
                      </td>
                      <td style={{ ...tdStyle, fontSize: '10px' }}>
                        {(customer.ulica || customer.adres) && <div>{customer.ulica || customer.adres}</div>}
                        {(customer.miasto || customer.kod_pocztowy) && (
                          <div style={{ color: 'var(--text-muted)' }}>{customer.kod_pocztowy} {customer.miasto}</div>
                        )}
                        {!customer.ulica && !customer.adres && !customer.miasto && <span style={{ color: 'var(--text-muted)' }}>—</span>}
                        {customer.wysylka_inna ? (
                          <div style={{
                            marginTop: '4px', padding: '2px 6px',
                            backgroundColor: 'var(--info-light)', borderRadius: '4px',
                            fontSize: '10px', color: 'var(--info)', display: 'inline-block'
                          }}>
                            📦 Inny adres wysyłki: {customer.wysylka_ulica}, {customer.wysylka_kod_pocztowy} {customer.wysylka_miasto}
                          </div>
                        ) : null}
                      </td>
                      <td style={tdStyle}><ConsentBadges customer={customer} /></td>
                      <td style={{ ...tdStyle, textAlign: 'center' }}>
                        <ActionMenu actions={[
                          { icon: '📋', label: 'Historia', color: 'var(--info)', onClick: () => { setSelectedCustomerForHistory(customer); setShowHistoryModal(true); } },
                          canEditCustomers && { icon: '✏️', label: 'Edytuj', color: 'var(--primary)', onClick: () => { setEditingCustomer(toEditModel(customer)); setShowEditModal(true); } },
                          canMergeCustomers && !scalona && { icon: '🔗', label: 'Scal', color: 'var(--warning)', onClick: () => openMerge(customer) },
                          canDeleteCustomers && !nieaktywna && { icon: '🚫', label: 'Dezaktywuj', color: 'var(--danger)', onClick: () => handleDeactivateCustomer(customer) },
                        ]} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Paginacja serwerowa */}
        {total > 0 && (
          <div style={{
            padding: '0.75rem', borderTop: '1px solid var(--border)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', justifyContent: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginRight: '1rem' }}>
                <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Pokaż:</span>
                <select
                  value={perPage}
                  onChange={(e) => setPerPage(parseInt(e.target.value, 10))}
                  style={{
                    padding: '6px 12px', fontSize: '12px', border: '1px solid var(--border)',
                    borderRadius: '6px', backgroundColor: 'var(--card-bg)', color: 'var(--text)'
                  }}
                >
                  <option value="10">10</option>
                  <option value="20">20</option>
                  <option value="30">30</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                </select>
              </div>

              <button onClick={() => handlePageChange(1)} disabled={currentPage === 1}
                style={{ ...btnStyle, backgroundColor: currentPage === 1 ? 'var(--bg)' : 'var(--card-bg)', color: currentPage === 1 ? 'var(--text-muted)' : 'var(--text-secondary)' }}
                title="Pierwsza strona">«</button>

              <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}
                style={{ ...btnStyle, backgroundColor: currentPage === 1 ? 'var(--bg)' : 'var(--card-bg)', color: currentPage === 1 ? 'var(--text-muted)' : 'var(--text-secondary)' }}>
                ← Poprzednia
              </button>

              <div style={{ display: 'flex', gap: '2px' }}>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) pageNum = i + 1;
                  else if (currentPage <= 3) pageNum = i + 1;
                  else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                  else pageNum = currentPage - 2 + i;
                  const active = currentPage === pageNum;
                  return (
                    <button key={pageNum} onClick={() => handlePageChange(pageNum)}
                      style={{
                        ...btnStyle, minWidth: '28px',
                        backgroundColor: active ? 'var(--primary)' : 'var(--card-bg)',
                        color: active ? '#fff' : 'var(--text-secondary)',
                        borderColor: active ? 'var(--primary)' : 'var(--border)',
                        fontWeight: active ? '600' : '400'
                      }}>
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage >= totalPages}
                style={{ ...btnStyle, backgroundColor: currentPage >= totalPages ? 'var(--bg)' : 'var(--card-bg)', color: currentPage >= totalPages ? 'var(--text-muted)' : 'var(--text-secondary)' }}>
                Następna →
              </button>

              <button onClick={() => handlePageChange(totalPages)} disabled={currentPage >= totalPages}
                style={{ ...btnStyle, backgroundColor: currentPage >= totalPages ? 'var(--bg)' : 'var(--card-bg)', color: currentPage >= totalPages ? 'var(--text-muted)' : 'var(--text-secondary)' }}
                title="Ostatnia strona">»</button>
            </div>
            <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>
              Wyświetlono {customers.length ? startIndex + 1 : 0} - {startIndex + customers.length} z {total} kontrahentów
            </div>
          </div>
        )}
      </div>

      {/* Modal scalania kart */}
      {mergeSource && (
        <div style={{
          position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10050
        }} onClick={closeMerge}>
          <div onClick={e => e.stopPropagation()} style={{
            backgroundColor: 'var(--card-bg)', borderRadius: '8px', width: '520px', maxWidth: '92vw',
            maxHeight: '86vh', overflowY: 'auto', padding: '1rem', boxShadow: '0 8px 24px rgba(0,0,0,0.25)'
          }}>
            <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: '0.5rem' }}>🔗 Scal karty kontrahenta</div>

            <div style={{
              padding: '0.5rem 0.75rem', backgroundColor: 'var(--warning-light)', color: 'var(--warning)',
              borderRadius: '4px', fontSize: '11px', marginBottom: '0.75rem'
            }}>
              ⚠️ Operacja przepina dokumenty; nazwa klienta na starych paragonach zmieni się.
              Faktury i dokumenty magazynowe zachowują własną kopię danych kontrahenta.
            </div>

            <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginBottom: '2px' }}>Karta źródłowa (zostanie oznaczona jako scalona)</div>
            <div style={{
              padding: '0.4rem 0.6rem', border: '1px solid var(--border)', borderRadius: '4px',
              fontSize: '12px', marginBottom: '0.75rem'
            }}>
              {displayName(mergeSource)} <span style={{ color: 'var(--text-muted)' }}>#{mergeSource.id}{mergeSource.nip ? ` | NIP ${mergeSource.nip}` : ''}</span>
            </div>

            <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginBottom: '2px' }}>Karta docelowa (przejmie dokumenty)</div>
            {mergeTarget ? (
              <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '0.4rem 0.6rem', border: '1px solid var(--success)', borderRadius: '4px',
                fontSize: '12px', marginBottom: '0.75rem'
              }}>
                <span>{displayName(mergeTarget)} <span style={{ color: 'var(--text-muted)' }}>#{mergeTarget.id}</span></span>
                <button onClick={() => setMergeTarget(null)} style={btnStyle}>Zmień</button>
              </div>
            ) : (
              <>
                <input
                  type="text"
                  value={mergeQuery}
                  onChange={e => setMergeQuery(e.target.value)}
                  placeholder="Szukaj karty docelowej (min. 2 znaki)..."
                  style={{
                    width: '100%', padding: '6px 12px', fontSize: '12px',
                    border: '1px solid var(--border)', borderRadius: '6px',
                    backgroundColor: 'var(--card-bg)', color: 'var(--text)', outline: 'none',
                    marginBottom: '0.5rem'
                  }}
                />
                {mergeResults.length > 0 && (
                  <div style={{
                    border: '1px solid var(--border)', borderRadius: '4px',
                    maxHeight: '180px', overflowY: 'auto', marginBottom: '0.75rem'
                  }}>
                    {mergeResults.map(r => (
                      <div key={r.id} onClick={() => setMergeTarget(r)} style={{
                        padding: '0.4rem 0.6rem', fontSize: '12px', cursor: 'pointer',
                        borderBottom: '1px solid var(--border)'
                      }}>
                        {r.name || displayName(r)} <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>#{r.id}{r.nip ? ` | NIP ${r.nip}` : ''}</span>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginBottom: '2px' }}>Powód scalenia (trafia do audytu)</div>
            <input
              type="text"
              value={mergePowod}
              onChange={e => setMergePowod(e.target.value)}
              placeholder="np. duplikat NIP założony w dwóch sklepach"
              style={{
                width: '100%', padding: '6px 12px', fontSize: '12px',
                border: '1px solid var(--border)', borderRadius: '6px',
                backgroundColor: 'var(--card-bg)', color: 'var(--text)', outline: 'none',
                marginBottom: '0.75rem'
              }}
            />

            {mergeError && (
              <div style={{
                padding: '0.5rem 0.75rem', backgroundColor: 'var(--danger-light)', color: 'var(--danger)',
                borderRadius: '4px', fontSize: '11px', marginBottom: '0.75rem'
              }}>❌ {mergeError}</div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
              <button onClick={closeMerge} style={{ ...btnStyle, padding: '6px 12px' }}>Anuluj</button>
              <button
                onClick={handleMergeConfirm}
                disabled={!mergeTarget || mergeBusy}
                style={{
                  padding: '6px 12px', fontSize: '11px', borderRadius: '4px', border: 'none',
                  cursor: (!mergeTarget || mergeBusy) ? 'not-allowed' : 'pointer', fontWeight: '600',
                  backgroundColor: (!mergeTarget || mergeBusy) ? 'var(--text-muted)' : 'var(--warning)',
                  color: '#fff'
                }}
              >
                {mergeBusy ? '⏳ Scalanie...' : '🔗 Scal karty'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modale karty */}
      {showAddModal && (
        <CustomerModal isEdit={false} customer={newCustomer} setCustomer={setNewCustomer}
          onSubmit={handleAddCustomer} onOpenExisting={handleOpenExisting}
          onClose={() => setShowAddModal(false)} />
      )}
      {showEditModal && editingCustomer && (
        <CustomerModal isEdit={true} customer={editingCustomer} setCustomer={setEditingCustomer}
          onSubmit={handleUpdateCustomer} onClose={() => { setShowEditModal(false); setEditingCustomer(null); }} />
      )}

      {/* Modal historii */}
      <CustomerHistoryModal
        customerId={selectedCustomerForHistory?.id}
        customerName={displayName(selectedCustomerForHistory)}
        customer={selectedCustomerForHistory}
        isOpen={showHistoryModal}
        onClose={() => { setShowHistoryModal(false); setSelectedCustomerForHistory(null); }}
      />
    </div>
  );
};

export default CustomersPage;
